import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowLeft, MapPin, Users, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RefereeSelectItems } from "@/components/calendar/RefereeSelectItems";

const fmtHHMM = (m) => `${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}`;

export default function PitchDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const [pitch, setPitch] = useState(null);
  const [avail, setAvail] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMinutes, setSelectedMinutes] = useState([]); // list of quarter-slot start minutes
  const [teams, setTeams] = useState([]);
  const [referees, setReferees] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [notes, setNotes] = useState("");
  const [sessionType, setSessionType] = useState("training");
  const [matchType, setMatchType] = useState("league");
  const [refId, setRefId] = useState("");
  const [opponent, setOpponent] = useState("");
  const [busy, setBusy] = useState(false);
  const [repeatWeeks, setRepeatWeeks] = useState(1);

  const dateStr = useMemo(() => format(selectedDate, "yyyy-MM-dd"), [selectedDate]);
  const [refereeBusy, setRefereeBusy] = useState([]);
  useEffect(() => { api.get("/referees/busy", { params: { date: dateStr } }).then(r => setRefereeBusy(r.data)).catch(() => setRefereeBusy([])); }, [dateStr]);

  useEffect(() => { (async () => {
    const { data } = await api.get(`/pitches/${id}`);
    setPitch(data);
    const [t, r] = await Promise.all([api.get("/teams"), api.get("/referees")]);
    setTeams(t.data);
    setReferees(r.data);
  })(); }, [id]);

  useEffect(() => { (async () => {
    if (!pitch) return;
    const { data } = await api.get(`/pitches/${id}/availability`, { params: { date_str: dateStr } });
    setAvail(data);
    setSelectedMinutes([]);
  })(); }, [id, dateStr, pitch]);

  const toggleMinute = (m) => {
    setSelectedMinutes(prev => {
      if (prev.includes(m)) return prev.filter(x => x !== m);
      const nextSet = [...prev, m].sort((a,b)=>a-b);
      // enforce contiguous 15-min chain
      for (let i = 1; i < nextSet.length; i++) {
        if (nextSet[i] !== nextSet[i-1] + 15) return [m];
      }
      return nextSet;
    });
  };

  const refNeeded = sessionType === "game" && (matchType === "league" || matchType === "cup");

  const book = async () => {
    if (selectedMinutes.length === 0) return toast.error("Select at least one 15-minute slot");
    if (!teamId) return toast.error("Please pick a team for this booking");
    if (refNeeded && !refId) return toast.error("Referee is required for league/cup matches");
    const start = selectedMinutes[0];
    const end = selectedMinutes[selectedMinutes.length - 1] + 15;
    setBusy(true);
    try {
      await api.post("/bookings", {
        pitch_id: id,
        booking_date: dateStr,
        start_hour: Math.floor(start / 60),
        start_min: start % 60,
        end_hour: Math.floor(end / 60),
        end_min: end % 60,
        team_id: teamId,
        notes: notes || null,
        session_type: sessionType,
        match_type: sessionType === "game" ? matchType : null,
        referee_id: refNeeded ? refId : null,
        opponent: sessionType === "game" ? (opponent || null) : null,
        repeat_weeks: parseInt(repeatWeeks) || 1,
      });
      toast.success(repeatWeeks > 1 ? `${repeatWeeks} weekly bookings requested — check bookings` : "Booking confirmed — check your email");
      nav("/app/bookings");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Booking failed");
    } finally { setBusy(false); }
  };

  if (!pitch) return <div className="opacity-60 font-display tracking-widest">LOADING…</div>;

  // Group quarters by hour for the slot grid
  const hoursMap = new Map();
  (avail?.slots || []).forEach(s => {
    const h = s.hour;
    if (!hoursMap.has(h)) hoursMap.set(h, []);
    hoursMap.get(h).push(s);
  });
  const hourList = Array.from(hoursMap.entries()).sort(([a],[b])=> a-b);

  const totalMinutes = selectedMinutes.length * 15;
  const start = selectedMinutes[0];
  const end = selectedMinutes.length ? selectedMinutes[selectedMinutes.length - 1] + 15 : null;

  return (
    <div>
      <button onClick={()=>nav(-1)} className="inline-flex items-center gap-2 opacity-60 hover:opacity-100 text-sm mb-6" data-testid="pitch-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <div className="rounded-md overflow-hidden">
            {pitch.image_url && <img src={pitch.image_url} alt={pitch.name} className="w-full h-64 object-cover" />}
          </div>
          <div className="mt-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">{pitch.surface}</div>
            <h1 className="font-display font-black text-4xl mt-2 leading-tight">{pitch.name}</h1>
            <p className="mt-4 opacity-70">{pitch.description}</p>
            <div className="mt-6 space-y-2 text-sm opacity-70">
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#83B68B]" /> {pitch.location}</div>
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#83B68B]" /> Capacity {pitch.capacity}</div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#83B68B]" /> {pitch.open_hour.toString().padStart(2,"0")}:00 — {pitch.close_hour.toString().padStart(2,"0")}:00</div>
            </div>
          </div>
        </div>

        <div className="md:col-span-7 space-y-6">
          <div className="card-tactical rounded-md p-6">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-3">Select date</div>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d)=>d && setSelectedDate(d)}
              disabled={(d)=>d < new Date(new Date().setHours(0,0,0,0))}
              data-testid="pitch-calendar"
              className="rounded-md"
            />
          </div>

          <div className="card-tactical rounded-md p-6">
            <div className="flex justify-between items-baseline mb-4">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase opacity-50">Time slots · 15-min blocks</div>
                <div className="font-display font-bold text-xl mt-1">{format(selectedDate, "EEEE, d MMMM yyyy")}</div>
              </div>
              <div className="text-xs opacity-50 hidden sm:block">Tap contiguous 15-min blocks</div>
            </div>
            <div className="space-y-2" data-testid="pitch-slots-grid">
              {hourList.map(([hour, quarters]) => (
                <div key={hour} className="grid grid-cols-5 gap-2 items-center">
                  <div className="text-[10px] tracking-widest uppercase opacity-60 pr-2 font-display">
                    {String(hour).padStart(2,"0")}:00
                  </div>
                  {quarters.map(s => {
                    const isSelected = selectedMinutes.includes(s.minute);
                    const cls = s.booked
                      ? "slot-booked pointer-events-none"
                      : isSelected
                        ? "bg-[#83B68B] text-white"
                        : "bg-muted hover:border-[#83B68B] border border-border";
                    return (
                      <button
                        key={s.minute}
                        disabled={s.booked}
                        onClick={()=>toggleMinute(s.minute)}
                        data-testid={`slot-${s.minute}`}
                        className={`${cls} py-2 rounded text-xs font-display tracking-wider transition-colors`}
                      >
                        {fmtHHMM(s.minute)}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="card-tactical rounded-md p-6 space-y-5">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2">Session type</div>
              <div className="grid grid-cols-2 gap-2">
                {[{v:"training", l:"Training"}, {v:"game", l:"Game"}].map(o => (
                  <button key={o.v} type="button" onClick={()=>setSessionType(o.v)} data-testid={`session-type-${o.v}`}
                    className={`py-3 rounded-md text-sm font-display tracking-widest uppercase transition-colors ${sessionType === o.v ? "bg-[#83B68B] text-white" : "bg-muted opacity-80 border border-border hover:border-[#83B68B]"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            {sessionType === "game" && (
              <>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2">Match type</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[{v:"league", l:"League"}, {v:"cup", l:"Cup"}, {v:"friendly", l:"Friendly"}].map(o => (
                      <button key={o.v} type="button" onClick={()=>setMatchType(o.v)} data-testid={`match-type-${o.v}`}
                        className={`py-3 rounded-md text-sm font-display tracking-widest uppercase transition-colors ${matchType === o.v ? "bg-[#83B68B] text-white" : "bg-muted opacity-80 border border-border hover:border-[#83B68B]"}`}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.3em] uppercase opacity-50">Opponent (optional)</Label>
                  <Input value={opponent} onChange={e=>setOpponent(e.target.value)} data-testid="opponent-input" placeholder="e.g. Stansted YFC U12" className="mt-2" />
                </div>
                {refNeeded && (
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2">Referee <span className="text-[#83B68B]">(required)</span></div>
                    <Select value={refId} onValueChange={setRefId}>
                      <SelectTrigger data-testid="referee-select"><SelectValue placeholder="Select a referee" /></SelectTrigger>
                      <SelectContent>
                        <RefereeSelectItems referees={referees} busy={refereeBusy} date={dateStr} isAdmin={user?.role === "admin"}
                          team={teams.find(t => t.id === teamId)}
                          start={selectedMinutes.length ? selectedMinutes[0] : null}
                          end={selectedMinutes.length ? selectedMinutes[selectedMinutes.length - 1] + 15 : null}
                          emptyText="No referees yet — admin can add them." />
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2">Team <span className="text-[#83B68B]">(required)</span></div>
                  <Select value={teamId} onValueChange={setTeamId}>
                    <SelectTrigger data-testid="pitch-team-select"><SelectValue placeholder="Select a team" /></SelectTrigger>
                    <SelectContent>
                      {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {t.age_group}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2">Notes</div>
              <Textarea value={notes} onChange={e=>setNotes(e.target.value)} data-testid="pitch-notes-input" placeholder="Anything the club should know" rows={3} />
            </div>

            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2">Repeat weekly</div>
              <div className="grid grid-cols-5 gap-2">
                {[1, 2, 4, 8, 12].map(n => (
                  <button key={n} type="button" onClick={()=>setRepeatWeeks(n)} data-testid={`repeat-${n}`}
                    className={`py-2.5 rounded-md text-sm font-display tracking-widest uppercase transition-colors ${repeatWeeks === n ? "bg-[#83B68B] text-white" : "bg-muted opacity-80 border border-border hover:border-[#83B68B]"}`}>
                    {n === 1 ? "Once" : `${n} wks`}
                  </button>
                ))}
              </div>
              {repeatWeeks > 1 && <div className="text-xs opacity-50 mt-2">Books this slot every week for {repeatWeeks} weeks. Weeks already booked will be skipped.</div>}
            </div>

            <div className="flex items-center justify-between border-t border-border pt-4">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase opacity-50">Session</div>
                <div className="font-display font-black text-3xl text-[#83B68B]" data-testid="pitch-total">
                  {totalMinutes > 0 ? `${fmtHHMM(start)} — ${fmtHHMM(end)}` : "—"}
                </div>
                <div className="text-xs opacity-40">{totalMinutes} min selected</div>
              </div>
              <button onClick={book} disabled={busy || totalMinutes === 0} className="btn-volt px-8 py-4 rounded-md disabled:opacity-40" data-testid="pitch-book-btn">
                {busy ? "Booking…" : "Confirm booking"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
